import {
  faArrowLeft,
  faArrowRight,
  faEye,
} from '@fortawesome/pro-regular-svg-icons'
import { moment } from '@seiue/moment'
import { Button, Modal, Toast } from '@seiue/ui'
import { isEmpty, pick } from '@seiue/util'
import { Form } from 'antd'
import React, { useEffect, useRef } from 'react'

import {
  OutlineReq,
  TaskReq,
  TaskStatusEnum,
  TaskTypeEnum,
  activityApi,
  courseApi,
} from 'packages/sdks-next/growp'

import { CommonBasicInfoRef, SubmitValues } from '@/components/CommonBasicInfo'
import { ContentCard } from '@/components/ContentCard'
import { ModalLayout } from '@/components/ModalLayout'
import { TaskHeaderLeft } from '@/components/TaskHeaderLeft'
import { UserCourse } from '@/features/UserTasks/Course/pages/UserCourse'

import { EditTaskModalProps } from '../types'

import { BasicInfo } from './BasicInfo'
import { SetCourse } from './SetCourse'
import { EditCourseTaskContext } from './context'
import { formatOutlines } from './utils'
import styled from 'styled-components'

/**
 * 编辑课程任务弹窗
 *
 * @param param0 - 参数
 * @param param0.onClose - 关闭弹窗
 * @param param0.taskId - 任务 ID
 * @param param0.activityId - 活动 ID
 * @param param0.activityTaskOptions - 活动任务选项
 * @returns 组件
 */
export const EditCourseTaskModal: React.FC<EditTaskModalProps> = ({
  onClose,
  taskId,
  activityId,
  activityTaskOptions,
  domain,
}) => {
  const [title, setTitle] = React.useState<string>('')
  const [currentStep, setCurrentStep] = React.useState<number>(0)
  const [curTaskId, setCurTaskId] = React.useState<number | undefined>(taskId)

  const [basicInfo, setBasicInfo] = React.useState<TaskReq>()

  const [commonBasicInfo, setCommonBasicInfo] = React.useState<SubmitValues>({})

  const [taskForm] = Form.useForm()

  const basicInfoRef = useRef<CommonBasicInfoRef>(null)

  const { data: courseTask } = courseApi.useLoadCourseActivityTask(
    {
      id: curTaskId || 0,
      query: {
        isForeground: false,
        expand: [
          'outlines',
          ['outlines', 'tasks'],
          ['outlines', 'tasks', 'attachments'],
        ],
      },
    },
    { disable: !curTaskId }
  )

  const { api: createCourseActivityTask } =
    courseApi.useCreateCourseActivityTask({
      reload: [
        courseApi.loadCourseActivityTask,
        activityApi.queryTasks,
        activityApi.loadActivity,
      ],
    })

  const { api: updateCourseActivityTask } =
    courseApi.useUpdateCourseActivityTask({
      reload: [
        courseApi.loadCourseActivityTask,
        activityApi.queryTasks,
        activityApi.loadActivity,
      ],
    })

  const isEdit = !!curTaskId

  const getTaskPayload = ({
    name,
    status = TaskStatusEnum.Editing,
    task,
  }: {
    name: string
    task?: Omit<TaskReq, 'activityId' | 'status' | 'type' | 'name'>
    status?: TaskStatusEnum
  }): TaskReq => {
    const commonBasicInfo = basicInfoRef.current?.getBasicInfo()
    return {
      ...task,
      ...commonBasicInfo,
      name,
      activityId,
      type: TaskTypeEnum.Course,
      status,
    }
  }

  useEffect(() => {
    taskForm.setFieldsValue({ ...courseTask?.task, ...basicInfo })
    setTitle(basicInfo?.name || courseTask?.task?.name || '')
  }, [courseTask, taskForm, basicInfo])

  const createIt = async ({ outlines }: { outlines?: OutlineReq[] }) => {
    const { data: newTask } = await createCourseActivityTask({
      task: basicInfo,
      outlines,
    })
    setCurTaskId(newTask?.task?.id)
  }

  const createActivityTask = async ({
    name,
    status = TaskStatusEnum.Editing,
    task,
  }: {
    name: string
    status?: TaskStatusEnum
    task?: Omit<TaskReq, 'activityId' | 'status' | 'type' | 'name'>
  }) => {
    const taskReq = getTaskPayload({ name, status, task })

    const { data: newTask } = await createCourseActivityTask({
      task: taskReq,
    })
    setCurTaskId(newTask?.task?.id)
  }

  useEffect(() => {
    setCommonBasicInfo({
      ...pick({ ...basicInfo, ...courseTask?.task }, [
        'endAt',
        'adminIds',
        'timedStartAt',
        'prevTaskIds',
      ]),
      startAt: courseTask?.task
        ? courseTask?.task.startAt
        : moment().format('YYYY-MM-DD HH:mm:ss'),
      isShow:
        courseTask?.task?.isShow !== undefined
          ? courseTask?.task?.isShow
          : true,
    })
  }, [courseTask?.task, basicInfo])

  const filterTaskOptions =
    activityTaskOptions?.filter((o) => o.value !== taskId) || []

  return (
    <ModalLayout
      title={
        <TaskHeaderLeft
          title={title || '未命名课程'}
          onClose={onClose}
        />
      }
      content={
        <EditCourseTaskContext.Provider
          value={{
            activityId,
            outlines: formatOutlines(courseTask?.outlines || []),
            curTaskId,
            setCurTaskId,
            createIt,
          }}
        >
          <ContentCard
            style={{ minHeight: 200 }}
            contentStyle={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}
          >
            {currentStep === 0 ? (
              <BasicInfo
                form={taskForm}
                setTitle={setTitle}
                basicInfoRef={basicInfoRef}
                commonBasicInfo={commonBasicInfo}
                activityTaskOptions={filterTaskOptions}
                isSubmit={!!courseTask?.isSubmit}
              />
            ) : (
              <SetCourse />
            )}
          </ContentCard>
          <StyledContentCard style={{ textAlign: 'center' }}>
            <Button
              iconRight={currentStep === 0 ? faArrowRight : undefined}
              icon={currentStep === 1 ? faArrowLeft : undefined}
              onClick={async () => {
                if (currentStep === 0) {
                  const commonRefInfo = basicInfoRef.current?.getBasicInfo()
                  if (isEmpty(commonRefInfo)) {
                    return
                  }
                  taskForm.validateFields().then(async (values) => {
                    const task: TaskReq = getTaskPayload({
                      name: values.name,
                      task: values,
                    })

                    setBasicInfo(task)
                    setCurrentStep(1)
                  })
                } else {
                  setBasicInfo(basicInfo)
                  setCommonBasicInfo(basicInfo || {})
                  setCurrentStep(0)
                }
              }}
            >
              {currentStep === 0 ? '下一步' : '上一步'}
            </Button>
            {currentStep === 1 ? (
              <Button
                icon={faEye}
                disabled={!courseTask?.outlines?.length}
                onClick={() => {
                  const { hide } = Modal.open({
                    header: null,
                    footer: null,
                    fullScreen: true,
                    bodyStyle: { padding: 0 },
                    children: (
                      <UserCourse
                        previewData={courseTask}
                        onPreviewClose={() => hide()}
                      />
                    ),
                  })
                }}
              >
                {'预览'}
              </Button>
            ) : null}
          </StyledContentCard>
        </EditCourseTaskContext.Provider>
      }
      stepsParams={{
        size: 'small',
        current: currentStep,
        items: [{ title: '基本信息' }, { title: '设置课程' }],
        onChange: () => {
          if (currentStep === 0) {
            const commonRefInfo = basicInfoRef.current?.getBasicInfo()
            if (isEmpty(commonRefInfo)) {
              return
            }
            taskForm.validateFields().then(async (values) => {
              const task: TaskReq = getTaskPayload({
                name: values.name,
                task: values,
              })

              setBasicInfo(task)
              setCurrentStep(1)
            })
          } else {
            setBasicInfo(basicInfo)
            setCommonBasicInfo(basicInfo || {})
            setCurrentStep(0)
          }
        },
      }}
      headerRight={
        <Button
          type="primary"
          onClick={async () => {
            /**
             * 关于发布
             * 第一步：基本信息
             * 还没有创建任务时，需要先创建任务
             * 已经创建任务时，这时更新编辑的数据
             * 第二步：设置课程
             * 就是更新操作
             */
            if (currentStep === 0) {
              const commonRefInfo = basicInfoRef.current?.getBasicInfo()
              if (isEmpty(commonRefInfo)) {
                return
              }
              taskForm.validateFields().then(async (val) => {
                if (isEdit) {
                  if (courseTask?.task) {
                    const task = getTaskPayload({
                      task: { ...courseTask?.task, ...val },
                      name: val.name,
                      status: TaskStatusEnum.Published,
                    })

                    await updateCourseActivityTask({
                      id: curTaskId,
                      payload: { task },
                    })

                    Toast.success('更新成功')
                    onClose()
                  }
                } else {
                  await createActivityTask({
                    name: val.name,
                    status: TaskStatusEnum.Published,
                    task: val,
                  })
                  onClose()
                }
              })
            } else {
              if (!basicInfo) return
              if (isEdit) {
                const taskReq = getTaskPayload({
                  task: basicInfo,
                  name: basicInfo.name || courseTask?.task?.name || '',
                  status: TaskStatusEnum.Published,
                })
                await updateCourseActivityTask({
                  id: curTaskId,
                  payload: { task: taskReq },
                })

                Toast.success('更新成功')
                onClose()
              } else {
                await createActivityTask({
                  name: basicInfo.name,
                  status: TaskStatusEnum.Published,
                  task: basicInfo,
                })
                onClose()
              }
            }
          }}
        >
          {isEdit ? '更新' : '确认'}
        </Button>
      }
    />
  )
}

const StyledContentCard = styled(ContentCard)`
  &&& {
    .ant-btn {
      width: 96px;
      &:first-child {
        margin-right: 12px;
      }
    }
    .ant-btn-default[disabled] {
      background-color: ${(props) => props.theme.background._3};
    }
  }
`
